import React, { Component } from "react";
import { withStyles } from "@mui/styles";
import { withTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { toast } from "react-toastify";
import { navigate } from "@reach/router";

const styles = () => ({
  root: {
    padding: "16px 20px",
  },
});

class BinOrderMappings extends Component {
  constructor(props) {
    super(props);
    this.searchDebounceTimeout = null;
    this.state = {
      loading: false,
      mappings: [],
      boxId: "",
      boxName: "",
      searchtext: "",
      page: 0,
      rowsPerPage: 10,
      orderBy: "bin_id",
      order: "asc",
      totalRecords: 0,
      selectedRowKeys: [],
      removeConfirmOpen: false,
      removingMappings: false,
    };
  }

  componentDidMount() {
    const searchParams = new URLSearchParams(window.location.search || "");
    const boxId = searchParams.get("boxId") || "";
    const boxName = searchParams.get("boxName") || "";

    this.setState(
      {
        boxId,
        boxName,
      },
      () => {
        if (boxId) {
          this.fetchMappings();
        }
      }
    );
  }

  componentWillUnmount() {
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
      this.searchDebounceTimeout = null;
    }
  }

  getAuthToken = () => {
    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      return (
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken
      ) || "";
    } catch (error) {
      return "";
    }
  };

  fetchMappings = async () => {
    const { boxId, searchtext, page, rowsPerPage, orderBy, order } = this.state;
    const { t } = this.props;
    if (!boxId) return;

    this.setState({ loading: true });
    try {
      const token = this.getAuthToken();
      const url =
        process.env.REACT_APP_BASEURL +
        `fastlane-goliath/stocked-items?boxId=${encodeURIComponent(
          boxId
        )}&searchtext=${encodeURIComponent(searchtext)}&pageNo=${page}&pageSize=${rowsPerPage}&sortColumn=${encodeURIComponent(
          orderBy
        )}&sortOrder=${encodeURIComponent(order)}`;

      const response = await fetch(url, {
        method: "GET",
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      const stockedItems =
        result && result.data && Array.isArray(result.data.stockedItems)
          ? result.data.stockedItems
          : [];
      const totalRecords =
        result && result.data && Number.isFinite(result.data.total)
          ? result.data.total
          : stockedItems.length;

      const mappings = stockedItems.map((row) => ({
        record_id: row && row.record_id,
        stock_code: row && row.stock_code,
        pu_code: row && row.pu_code,
        bin_id: row && row.bin_id,
        position_id:
          (row && row.position_id) ||
          (row && row.positionId) ||
          (row && row.slot_position_id) ||
          null,
        patient_name: row && row.patient_name,
        status: row && row.status,
        domain: row && row.domain,
      }));

      this.setState({ mappings, totalRecords, selectedRowKeys: [], loading: false });
    } catch (error) {
      this.setState({ loading: false });
      toast.error(
        (error && error.message) || (t("Location.BinOrderMappingsLoadFailed") || "Failed to load bin-order mappings.")
      );
    }
  };

  handleBack = () => {
    navigate("/home/manage/locations");
  };

  handleSearchChange = (event) => {
    const nextSearch = event.target.value || "";
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
      this.searchDebounceTimeout = null;
    }

    this.setState({
      searchtext: nextSearch,
      page: 0,
    });

    const trimmedSearch = nextSearch.trim();

    // Reset to unfiltered data when search is cleared.
    if (trimmedSearch.length === 0) {
      this.fetchMappings();
      return;
    }

    // Start search only after 3+ characters.
    if (trimmedSearch.length < 3) {
      return;
    }

    this.searchDebounceTimeout = setTimeout(() => {
      this.fetchMappings();
    }, 400);
  };

  handleRequestSort = (property) => {
    this.setState(
      (prev) => {
        const isSameColumn = prev.orderBy === property;
        const nextOrder = isSameColumn && prev.order === "asc" ? "desc" : "asc";
        return {
          orderBy: property,
          order: nextOrder,
          page: 0,
        };
      },
      this.fetchMappings
    );
  };

  handleChangePage = (_event, page) => {
    this.setState({ page }, this.fetchMappings);
  };

  handleChangeRowsPerPage = (event) => {
    const nextRows = parseInt(event.target.value, 10);
    this.setState(
      {
        rowsPerPage: Number.isFinite(nextRows) ? nextRows : 10,
        page: 0,
      },
      this.fetchMappings
    );
  };

  getRowKey = (row) => `${row.domain}:${row.record_id}`;

  isRowSelected = (row) => this.state.selectedRowKeys.includes(this.getRowKey(row));

  handleToggleRow = (row) => {
    const rowKey = this.getRowKey(row);
    this.setState((prev) => {
      const isSelected = prev.selectedRowKeys.includes(rowKey);
      return {
        selectedRowKeys: isSelected
          ? prev.selectedRowKeys.filter((key) => key !== rowKey)
          : [...prev.selectedRowKeys, rowKey],
      };
    });
  };

  handleToggleSelectAll = (event) => {
    const checked = event && event.target && event.target.checked;
    if (!checked) {
      this.setState({ selectedRowKeys: [] });
      return;
    }
    const allKeys = this.state.mappings.map((row) => this.getRowKey(row));
    this.setState({ selectedRowKeys: allKeys });
  };

  openRemoveConfirm = () => {
    this.setState({ removeConfirmOpen: true });
  };

  closeRemoveConfirm = () => {
    if (this.state.removingMappings) return;
    this.setState({ removeConfirmOpen: false });
  };

  removeSelectedMappings = async () => {
    const { boxId, mappings, selectedRowKeys } = this.state;
    const { t } = this.props;
    if (!boxId || selectedRowKeys.length === 0) return;

    const selectedItems = mappings
      .filter((row) => selectedRowKeys.includes(this.getRowKey(row)))
      .map((row) => ({
        domain: row.domain,
        record_id: row.record_id,
      }));

    if (selectedItems.length === 0) return;

    this.setState({ removingMappings: true });
    try {
      const token = this.getAuthToken();
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "fastlane-goliath/stocked-items/remove-mappings",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            boxId,
            items: selectedItems,
          }),
        }
      );
      const result = await response.json();
      const removedCount =
        result && result.data && Number.isFinite(result.data.removedCount)
          ? result.data.removedCount
          : 0;
      const failedCount =
        result && result.data && Number.isFinite(result.data.failedCount)
          ? result.data.failedCount
          : 0;

      if (removedCount > 0) {
        toast.success(
          t("Location.BOM.RemoveSuccess", { count: removedCount }) ||
            `${removedCount} mappings removed successfully.`
        );
      }
      if (failedCount > 0) {
        toast.error(
          t("Location.BOM.RemovePartialFailure", { count: failedCount }) ||
            `${failedCount} mappings could not be removed.`
        );
      }

      this.setState(
        {
          removingMappings: false,
          removeConfirmOpen: false,
          selectedRowKeys: [],
        },
        this.fetchMappings
      );
    } catch (error) {
      this.setState({ removingMappings: false });
      toast.error(
        (error && error.message) ||
          t("Location.BOM.RemoveFailed") ||
          "Failed to remove selected mappings."
      );
    }
  };

  render() {
    const { t, classes } = this.props;
    const {
      loading,
      mappings,
      boxName,
      boxId,
      searchtext,
      page,
      rowsPerPage,
      order,
      orderBy,
      totalRecords,
      selectedRowKeys,
      removeConfirmOpen,
      removingMappings,
    } = this.state;
    const headCells = [
      { id: "stock_code", label: t("Location.BOM.StockCode") || "Stock Code" },
      { id: "pu_code", label: t("Location.BOM.PuCode") || "PU Code / Pickup Code" },
      { id: "bin_id", label: t("Location.BOM.BinId") || "Bin ID" },
      { id: "position_id", label: t("Location.BOM.PositionId") || "Slot / Position ID" },
      { id: "patient_name", label: t("Location.BOM.Patient") || "Patient" },
      { id: "status", label: t("Location.BOM.Status") || "Status" },
      { id: "domain", label: t("Location.BOM.Domain") || "Domain" },
    ];

    const allRowsSelected =
      mappings.length > 0 && selectedRowKeys.length === mappings.length;
    const hasSelectedRows = selectedRowKeys.length > 0;

    return (
      <div className={classes.root}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t("Location.BinOrderMappings") || "Bin Order Mappings"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
              {(t("Location.BinOrderMappingsFor") || "Location") +
                ": " +
                (boxName || boxId || "-")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="error"
              disabled={!hasSelectedRows || loading || removingMappings}
              onClick={this.openRemoveConfirm}
            >
              {t("Location.BOM.RemoveSelected") || "Remove Selected Mappings"}
            </Button>
            <Button variant="outlined" onClick={this.handleBack}>
              {t("Location.BackToManageLocations") || "Back to Manage Locations"}
            </Button>
            <Button variant="contained" onClick={this.fetchMappings} disabled={loading || !boxId}>
              {t("Location.Refresh") || "Refresh"}
            </Button>
          </Box>
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            value={searchtext}
            onChange={this.handleSearchChange}
            placeholder={t("Location.BOM.SearchPlaceholder") || "Search by stock code, bin, patient, status, domain"}
          />
        </Box>

        {loading ? (
          <Box
            sx={{
              minHeight: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <React.Fragment>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allRowsSelected}
                      indeterminate={!allRowsSelected && hasSelectedRows}
                      onChange={this.handleToggleSelectAll}
                      inputProps={{ "aria-label": "select all mappings" }}
                    />
                  </TableCell>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id}>
                      <Tooltip title={t("Global.Label.Filter.Sort") || "Sort"}>
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={order}
                          onClick={() => this.handleRequestSort(headCell.id)}
                        >
                          {headCell.label}
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {mappings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {t("Location.BinOrderMappingsEmpty") ||
                        "No current bin-order mappings found for this location."}
                    </TableCell>
                  </TableRow>
                ) : (
                  mappings.map((row) => (
                    <TableRow key={`${row.domain}-${row.bin_id}-${row.stock_code || ""}`}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={this.isRowSelected(row)}
                          onChange={() => this.handleToggleRow(row)}
                          inputProps={{ "aria-label": "select mapping row" }}
                        />
                      </TableCell>
                      <TableCell>{row.stock_code || "-"}</TableCell>
                      <TableCell>{row.pu_code || "-"}</TableCell>
                      <TableCell>{row.bin_id}</TableCell>
                      <TableCell>{row && row.position_id ? row.position_id : "-"}</TableCell>
                      <TableCell>{row.patient_name || "-"}</TableCell>
                      <TableCell>{row.status || "-"}</TableCell>
                      <TableCell>{row.domain || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalRecords || 0}
              page={page}
              onPageChange={this.handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={this.handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </React.Fragment>
        )}
        <Dialog open={removeConfirmOpen} onClose={this.closeRemoveConfirm}>
          <DialogTitle>
            {t("Location.BOM.RemoveConfirmTitle") || "Remove selected mappings?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("Location.BOM.RemoveConfirmMessage", { count: selectedRowKeys.length }) ||
                `This will remove bin assignments for ${selectedRowKeys.length} selected mapped orders. Order status will not change.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeRemoveConfirm} disabled={removingMappings}>
              {t("Location.CancelButton") || "Cancel"}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={this.removeSelectedMappings}
              disabled={removingMappings}
            >
              {removingMappings
                ? t("Location.BOM.Removing") || "Removing..."
                : t("Location.BOM.ConfirmRemove") || "Confirm Remove"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default withStyles(styles)(withTranslation()(BinOrderMappings));



// WEBPACK FOOTER //
// ./src/components/bin-order-mappings/index.js